import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchRequests, getPresignedUrl } from '../services/api';
import './RequestsPage.css';

const RequestsPage = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingResults, setLoadingResults] = useState({});
  const [copySuccess, setCopySuccess] = useState(false);
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [recordingTitle, setRecordingTitle] = useState('');
  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState('');
  const [channelWarning, setChannelWarning] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [showFileModal, setShowFileModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileTitle, setFileTitle] = useState('');
  const [audioLoading, setAudioLoading] = useState({});
  const mediaRecorderRef = useRef(null);
  const audioStreamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const getRequests = async () => {
      try {
        setLoading(true);
        const data = await fetchRequests();
        setRequests(data['data']);
        setError(null);
      } catch (error) {
        console.error('Tasks 가져오는 중 오류 발생:', error);
        setError('Tasks 불러오는데 실패했습니다.');
        
        // 인증 오류인 경우 로그인 페이지로 리다이렉트
        if (error.message.includes('인증') || error.message.includes('auth')) {
          navigate('/signin');
        }
      } finally {
        setLoading(false);
      }
    };

    const loadChannels = async () => {
      try {
        const { fetchChannels } = await import('../services/api');
        const result = await fetchChannels();
        
        if (result.success) {
          setChannels(result.channels);
        }
      } catch (error) {
        console.error('채널 목록 로드 오류:', error);
      }
    };

    getRequests();
    loadChannels();
  }, [navigate]);

  const handleChannelChange = (channelValue) => {
    setSelectedChannel(channelValue);
    
    if (channelValue) {
      const selectedChannelData = channels.find(ch => 
        `${ch.channel_type}:${ch.channel_id}` === channelValue
      );
      
      if (selectedChannelData && !selectedChannelData.project) {
        setChannelWarning('해당 채널에는 프로젝트가 설정되어 있지 않습니다. 이경우 KB 활용 및 고품질의 STT 결과를 얻을수 없습니다.');
      } else {
        setChannelWarning('');
      }
    } else {
      setChannelWarning('');
    }
  };

  const getSelectedChannelProject = () => {
    if (!selectedChannel) return null;
    const selectedChannelData = channels.find(ch => 
      `${ch.channel_type}:${ch.channel_id}` === selectedChannel
    );
    return selectedChannelData?.project || null;
  };

  const startTimer = () => {
    timerRef.current = setInterval(() => {
      setRecordingTime(prev => prev + 1);
    }, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setRecordingTime(0);
  };

  const handleStartRecording = async () => {
    try {
      setError(null);
      setShowRecordModal(false);
      
      // 마이크 권한 요청
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 44100,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      audioStreamRef.current = stream;
      chunksRef.current = [];

      // Safari 최적화된 설정
      const options = {
        mimeType: 'audio/mp4',
        audioBitsPerSecond: 128000
      };

      // MediaRecorder 생성
      mediaRecorderRef.current = new MediaRecorder(stream, options);

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(chunksRef.current, { type: 'audio/mp4' });
        await uploadRecording(audioBlob);
      };

      // 녹음 시작
      mediaRecorderRef.current.start(1000);
      setIsRecording(true);
      startTimer();

    } catch (error) {
      console.error('녹음 시작 실패:', error);
      setError('마이크 접근 권한이 필요합니다. 브라우저 설정을 확인해주세요.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      stopTimer();
      
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => track.stop());
      }
    }
  };

  const uploadRecording = async (audioBlob) => {
    try {
      setUploadStatus('업로드 중...');
      
      const AudioService = (await import('../services/audioService')).default;
      const audioService = new AudioService();
      const metadata = {
        duration: recordingTime,
        timestamp: new Date().toISOString(),
        title: recordingTitle || '음성 녹음',
        channel: selectedChannel || '',
        mimeType: audioBlob.type || 'audio/mp4',
        size: audioBlob.size
      };

      await audioService.uploadAudioChunked(audioBlob, metadata);
      setUploadStatus('업로드 완료');
      
      // 목록 새로고침
      const data = await fetchRequests();
      setRequests(data['data']);
      
      // 상태 초기화
      setTimeout(() => {
        setUploadStatus('');
        setRecordingTitle('');
        setSelectedChannel('');
        setChannelWarning('');
      }, 2000);
      
    } catch (error) {
      console.error('업로드 실패:', error);
      setUploadStatus('업로드 실패');
      setError('녹음 업로드에 실패했습니다.');
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setFileTitle(file.name.replace(/\.[^/.]+$/, ''));
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile || !fileTitle.trim() || !selectedChannel) return;
    
    try {
      setUploadStatus('파일 업로드 중...');
      setShowFileModal(false);
      
      const AudioService = (await import('../services/audioService')).default;
      const audioService = new AudioService();
      const metadata = {
        timestamp: new Date().toISOString(),
        title: fileTitle,
        channel: selectedChannel,
        originalName: selectedFile.name
      };

      await audioService.uploadAudioChunked(selectedFile, metadata);
      setUploadStatus('업로드 완료');
      
      // 목록 새로고침
      const data = await fetchRequests();
      setRequests(data['data']);
      
      // 상태 초기화
      setTimeout(() => {
        setUploadStatus('');
        setFileTitle('');
        setSelectedFile(null);
        setSelectedChannel('');
        setChannelWarning('');
      }, 2000);
      
    } catch (error) {
      console.error('파일 업로드 실패:', error);
      setUploadStatus('업로드 실패');
      setError('파일 업로드에 실패했습니다.');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // 날짜 포맷팅 함수
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // 상태에 따른 스타일 클래스 반환
  const getStatusClass = (status) => {
    if (!status) return 'status-other';
    
    const statusLower = status.toLowerCase();
    if (statusLower === 'done') return 'status-done';
    if (statusLower.includes('failed')) return 'status-failed';
    return 'status-other';
  };

  // PK 클릭 시 클립보드에 복사
  const handleCopyPK = (pk) => {
    navigator.clipboard.writeText(pk)
      .then(() => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      })
      .catch(err => {
        console.error('클립보드 복사 실패:', err);
      });
  };

  // 결과물 보기 버튼 클릭 핸들러
  const handleViewResult = async (shortenPk, requestId) => {
    try {
      // 로딩 상태 설정
      setLoadingResults(prev => ({ ...prev, [requestId]: true }));
      
      // pre-signed URL 요청
      const response = await getPresignedUrl(shortenPk);
      const data = response['data']
      
      // 새 창에서 URL 열기
      if (data && data.url) {
        window.open(data.url, '_blank');
      } else {
        throw new Error('유효한 URL을 받지 못했습니다.');
      }
    } catch (error) {
      console.error('결과물을 가져오는 중 오류 발생:', error);
      alert('결과물을 불러오는데 실패했습니다.');
    } finally {
      // 로딩 상태 해제
      setLoadingResults(prev => ({ ...prev, [requestId]: false }));
    }
  };

  // 결과 토글 핸들러는 삭제 - 결과가 항상 표시됨

  // 상태와 결과물 버튼을 함께 표시
  const renderStatusWithButton = (request) => {
    const { shortenPk, PK, status, result } = request;
    const isLoading = loadingResults[PK];
    // 대소문자 구분 없이 'done'인지 확인
    const isDone = status && (status.toLowerCase() === 'done');
    const isExpanded = expandedResults[PK];
    
    return (
      <div className="status-with-button">
        <span className={`status-badge ${getStatusClass(status)}`}>
          {status}
        </span>
        
        {isDone && (
          <div className="result-button-container">
            {isLoading ? (
              <div className="loading-container">
                <div className="progress-bar">
                  <div className="progress-bar-inner"></div>
                </div>
                <span className="loading-text">로딩 중...</span>
              </div>
            ) : (
              <>
                <button 
                  className="view-result-button"
                  onClick={() => toggleResult(PK)}
                >
                  {isExpanded ? '결과 숨기기' : '결과 보기'}
                </button>
                {shortenPk && (
                  <button 
                    className="view-result-button"
                    onClick={() => handleViewResult(shortenPk, PK)}
                  >
                    파일 보기
                  </button>
                )}
              </>
            )}
          </div>
        )}
        
        {isExpanded && result && (
          <div className="result-content">
            <h4>결과:</h4>
            <pre>{result}</pre>
          </div>
        )}
      </div>
    );
  };

  // 정보 컬럼 렌더링
  const renderInfoColumn = (request) => {
    const isDone = request.status && request.status.toLowerCase() === 'done';
    const isAudioRecord = request.type === 'audio_record';
    
    return (
      <div className="info-column">
        <div className="info-row status-row">
          <span className={`status-badge ${getStatusClass(request.status)}`}>
            {request.status}
          </span>
        </div>
      </div>
    );
  };

  // URL을 하이퍼링크로 변환하는 함수
  const convertUrlsToLinks = (text) => {
    if (!text) return '';
    
    // URL 정규식 패턴 (http, https로 시작하는 URL)
    const urlPattern = /(https?:\/\/[^\s]+)/g;
    
    // 텍스트를 분할하여 URL 부분은 링크로 변환
    const parts = text.split(urlPattern);
    const matches = text.match(urlPattern) || [];
    
    return parts.map((part, index) => {
      // 짝수 인덱스는 일반 텍스트, 홀수 인덱스는 URL에 해당하는 부분
      if (index % 2 === 0) {
        return part;
      } else {
        const url = matches[(index - 1) / 2];
        return (
          <a 
            key={index} 
            href={url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="request-link"
          >
            {url}
          </a>
        );
      }
    });
  };

  // 전사 원천소스 보기
  const showTranscriptionResult = async (request) => {
    const loadingKey = `${request.PK}_transcription`;
    try {
      setAudioLoading(prev => ({ ...prev, [loadingKey]: true }));
      
      const { fetchAudioTranscription } = await import('../services/api');
      const response = await fetchAudioTranscription(request.PK);
      
      const newTab = window.open('', '_blank');
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>전사 원천소스 - ${request.PK}</title>
          <meta charset="utf-8">
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
              line-height: 1.6;
              max-width: 800px;
              margin: 0 auto;
              padding: 20px;
              color: #333;
            }
            .transcription { 
              background: #f8f9fa; 
              padding: 15px; 
              border-radius: 5px; 
              white-space: pre-wrap; 
              word-wrap: break-word;
              border-left: 4px solid #007bff;
            }
            h1 { color: #007bff; }
          </style>
        </head>
        <body>
          <h1>전사 원천소스</h1>
          <div class="transcription">${response.transcription || response.data || '전사 내용을 찾을 수 없습니다.'}</div>
        </body>
        </html>
      `;
      
      newTab.document.write(htmlContent);
      newTab.document.close();
    } catch (error) {
      console.error('전사 내용 로드 실패:', error);
      alert('전사 내용을 불러오는데 실패했습니다.');
    } finally {
      setAudioLoading(prev => ({ ...prev, [loadingKey]: false }));
    }
  };

  // 요약 문서 보기
  const showSummaryResult = async (request) => {
    const loadingKey = `${request.PK}_summary`;
    try {
      setAudioLoading(prev => ({ ...prev, [loadingKey]: true }));
      
      const { fetchAudioSummary } = await import('../services/api');
      const response = await fetchAudioSummary(request.PK);
      
      const newTab = window.open('', '_blank');
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>요약 문서 - ${request.PK}</title>
          <meta charset="utf-8">
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
              line-height: 1.6;
              max-width: 800px;
              margin: 0 auto;
              padding: 20px;
              color: #333;
            }
            .summary { 
              background: #f0f8f0; 
              padding: 15px; 
              border-radius: 5px; 
              white-space: pre-wrap; 
              word-wrap: break-word;
              border-left: 4px solid #28a745;
            }
            h1 { color: #28a745; }
          </style>
        </head>
        <body>
          <h1>요약 문서</h1>
          <div class="summary">${response.summary || response.data || '요약 문서를 찾을 수 없습니다.'}</div>
        </body>
        </html>
      `;
      
      newTab.document.write(htmlContent);
      newTab.document.close();
    } catch (error) {
      console.error('요약 문서 로드 실패:', error);
      alert('요약 문서를 불러오는데 실패했습니다.');
    } finally {
      setAudioLoading(prev => ({ ...prev, [loadingKey]: false }));
    }
  };

  // Query 결과를 새 탭에 표시
  const showQueryResult = (request) => {
    const newTab = window.open('', '_blank');
    
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Query Result - ${request.PK}</title>
        <meta charset="utf-8">
        <style>
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
            line-height: 1.6;
            max-width: 800px;
            margin: 0 auto;
            padding: 20px;
            color: #333;
          }
          .query { 
            background: #f0f8ff; 
            padding: 15px; 
            border-radius: 5px; 
            margin-bottom: 20px;
            border-left: 4px solid #007bff;
          }
          .result { 
            background: #f8f9fa; 
            padding: 15px; 
            border-radius: 5px; 
            white-space: pre-wrap; 
            word-wrap: break-word;
            border-left: 4px solid #28a745;
          }
          h2 { color: #007bff; }
          h3 { color: #28a745; }
        </style>
      </head>
      <body>
        <h1>Query Result</h1>
        <h2>Question:</h2>
        <div class="query">${request.request}</div>
        <h3>Answer:</h3>
        <div class="result">${request.result || '결과가 아직 준비되지 않았습니다.'}</div>
      </body>
      </html>
    `;
    
    newTab.document.write(htmlContent);
    newTab.document.close();
  };

  // type에 따라 요청 내용을 다르게 렌더링
  const renderRequestContent = (request) => {
    const isDone = request.status && request.status.toLowerCase() === 'done';
    const isAudioRecord = request.type === 'audio_record';
    
    if (request.type === 'audio_record') {
      return (
        <div className="audio-content">
          <div className="audio-title-row">
            <span className="request-text">
              {request.request || '오디오 녹음'}
            </span>
            {isDone && (
              <div className="audio-inline-actions">
                <button 
                  className="audio-inline-button transcription-btn"
                  onClick={() => showTranscriptionResult(request)}
                  title="전사 원천소스 보기"
                  disabled={audioLoading[`${request.PK}_transcription`]}
                >
                  {audioLoading[`${request.PK}_transcription`] ? (
                    <div className="inline-spinner"></div>
                  ) : (
                    '📄'
                  )}
                </button>
                <button 
                  className="audio-inline-button summary-btn"
                  onClick={() => showSummaryResult(request)}
                  title="요약 문서 보기"
                  disabled={audioLoading[`${request.PK}_summary`]}
                >
                  {audioLoading[`${request.PK}_summary`] ? (
                    <div className="inline-spinner"></div>
                  ) : (
                    '📋'
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      );
    } else {
      // query type - 하이퍼링크 스타일로 표시
      return (
        <div className="request-text">
          <span 
            className="query-link"
            onClick={() => showQueryResult(request)}
            title="클릭하여 결과 보기"
          >
            {convertUrlsToLinks(request.request)}
          </span>
        </div>
      );
    }
  };

  return (
    <div className="requests-container">
      <h2>Tasks</h2>
      
      {loading ? (
        <div className="loading">데이터를 불러오는 중...</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : (
        <>
          <div className="table-container">
            <table className="requests-table">
              <colgroup>
                <col className="pk-column" />
                <col style={{ width: '80%' }} />
                <col className="info-column" />
              </colgroup>
              <thead>
                <tr>
                  <th>Model</th>
                  <th>Content</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {requests.length > 0 ? (
                  requests.map((request, index) => (
                    <tr key={index}>
                      <td>
                        <div className="pk-container">
                          <div className="pk-display">
                            <span className="pk-icon">
                              {request.type === 'audio_record' ? '🎤' : '💬'}
                            </span>
                            <span 
                              className="pk-value" 
                              onClick={() => handleCopyPK(request.PK)}
                              title="클릭하여 복사"
                            >
                              {request.type === 'audio_record' ? 'Audio Record' : 'Query'}
                            </span>
                          </div>
                          <div className="time-row">
                            {formatDate(request.updatedAt)}
                          </div>
                        </div>
                      </td>
                      <td className="request-content">
                        {renderRequestContent(request)}
                      </td>
                      <td>{renderInfoColumn(request)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" className="no-data">요청 내역이 없습니다.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
      {copySuccess && <div className="copy-success">PK가 클립보드에 복사되었습니다!</div>}
      
      {/* 플로팅 버튼들 */}
      <div className="floating-buttons">
        {/* 파일 업로드 버튼 */}
        <button 
          className="floating-upload-button"
          onClick={() => setShowFileModal(true)}
          title="파일 업로드"
        >
          📁
        </button>
        
        {/* 마이크 버튼 */}
        {!isRecording ? (
          <button 
            className="floating-mic-button"
            onClick={() => setShowRecordModal(true)}
            title="음성 녹음 시작"
          >
            🎤
          </button>
        ) : (
          <button 
            className="floating-mic-button recording"
            onClick={stopRecording}
            title="녹음 중지"
          >
            ⏹️
          </button>
        )}
      </div>
      
      {/* 녹음 상태 표시 */}
      {isRecording && (
        <div className="recording-status-overlay">
          <div className="recording-indicator">
            <div className="pulse-dot"></div>
            <span>녹음 중</span>
            <div className="recording-time">{formatTime(recordingTime)}</div>
          </div>
        </div>
      )}
      
      {/* 업로드 상태 */}
      {uploadStatus && (
        <div className="upload-status">
          {uploadStatus}
        </div>
      )}

      {/* 녹음 시작 모달 */}
      {showRecordModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>음성 녹음 시작</h3>
            <input
              type="text"
              value={recordingTitle}
              onChange={(e) => setRecordingTitle(e.target.value)}
              placeholder="녹음 제목을 입력하세요"
              className="title-input"
              autoFocus
            />
            
            <div className="form-group">
              <label>알림 채널 (맵핑된 프로젝트에서 컨텍스트를 참조합니다)</label>
              <select
                value={selectedChannel}
                onChange={(e) => handleChannelChange(e.target.value)}
                className="channel-select"
              >
                <option value="">채널을 선택하세요</option>
                {channels.map((channel) => (
                  <option key={channel.pk} value={`${channel.channel_type}:${channel.channel_id}`}>
                    {channel.channel_name}
                  </option>
                ))}
              </select>
            </div>
            
            {getSelectedChannelProject() && (
              <div className="project-info">
                연결된 프로젝트: {getSelectedChannelProject()}
              </div>
            )}
            
            {channelWarning && (
              <div className="channel-warning">
                {channelWarning}
              </div>
            )}
            
            <div className="modal-actions">
              <button 
                className="btn btn-cancel"
                onClick={() => {
                  setShowRecordModal(false);
                  setRecordingTitle('');
                  setSelectedChannel('');
                  setChannelWarning('');
                }}
              >
                취소
              </button>
              <button 
                className="btn btn-confirm"
                onClick={handleStartRecording}
                disabled={!recordingTitle.trim() || !selectedChannel}
              >
                녹음 시작
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 파일 업로드 모달 */}
      {showFileModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>파일 업로드</h3>
            
            <div className="form-group">
              <label>파일 선택</label>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept="audio/*,video/*,.pdf,.doc,.docx,.txt"
                className="file-input"
              />
            </div>
            
            {selectedFile && (
              <div className="selected-file-info">
                선택된 파일: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
              </div>
            )}
            
            <input
              type="text"
              value={fileTitle}
              onChange={(e) => setFileTitle(e.target.value)}
              placeholder="파일 제목을 입력하세요"
              className="title-input"
            />
            
            <div className="form-group">
              <label>알림 채널 (맵핑된 프로젝트에서 컨텍스트를 참조합니다)</label>
              <select
                value={selectedChannel}
                onChange={(e) => handleChannelChange(e.target.value)}
                className="channel-select"
              >
                <option value="">채널을 선택하세요</option>
                {channels.map((channel) => (
                  <option key={channel.pk} value={`${channel.channel_type}:${channel.channel_id}`}>
                    {channel.channel_name}
                  </option>
                ))}
              </select>
            </div>
            
            {getSelectedChannelProject() && (
              <div className="project-info">
                연결된 프로젝트: {getSelectedChannelProject()}
              </div>
            )}
            
            {channelWarning && (
              <div className="channel-warning">
                {channelWarning}
              </div>
            )}
            
            <div className="modal-actions">
              <button 
                className="btn btn-cancel"
                onClick={() => {
                  setShowFileModal(false);
                  setFileTitle('');
                  setSelectedFile(null);
                  setSelectedChannel('');
                  setChannelWarning('');
                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                }}
              >
                취소
              </button>
              <button 
                className="btn btn-confirm"
                onClick={handleFileUpload}
                disabled={!selectedFile || !fileTitle.trim() || !selectedChannel}
              >
                업로드
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequestsPage;
